/** Device-local settings kept in localStorage (data dictionary §7). Never synchronised. */
export const StorageKey = {
  DeviceId: 'wiztime.deviceId',
  Locale: 'wiztime.locale',
  Onboarded: 'wiztime.onboarded',
  LastExportAt: 'wiztime.lastExportAt',
  /** Epoch milliseconds when this device first ran the app; the export reminder counts days of use from it. */
  FirstUseAt: 'wiztime.firstUseAt',
  /** Epoch milliseconds when the user dismissed the export reminder; it stays quiet for a while. */
  ExportReminderDismissedAt: 'wiztime.exportReminderDismissedAt',
  /** '1' once the user dismissed the "persistent storage was denied" notice. */
  PersistNoticeDismissed: 'wiztime.persistNoticeDismissed',
  /** '1' once the first-overlap notice was shown: a local create, move or resize overlapped (data dictionary §7). */
  OverlapNoticeDone: 'wiztime.overlapNoticeDone',
  /** Gesture learning counters (JSON), see `features/day/gestureCount.ts` and data dictionary §7. */
  GestureCount: 'wiztime.gestureCount',
  /** '1' once the "how to record" hint of an empty day view is finished: a block exists on this device, or the user closed it. */
  RecordHintDone: 'wiztime.recordHintDone',
  /** JSON array, a permutation of `timeline`, `quickMark`, `checkin`: left-to-right column order of the wide day view. */
  ColumnOrder: 'wiztime.columnOrder',
  /** Entries per page of Settings → Operation records: one of `OPLOG_PAGE_SIZES` (data dictionary §7). */
  OplogPageSize: 'wiztime.oplogPageSize',
  /** '1' when check-in reminders are on for this device (data dictionary §7); off by default. */
  ReminderEnabled: 'wiztime.reminderEnabled',
  /** JSON array of parent project ids the user collapsed in the quick-mark card; parents not listed are expanded (data dictionary §7). */
  QuickMarkCollapsed: 'wiztime.quickMarkCollapsed',
  /** JSON `{ fired, snoozed }`: reminders already shown and postponed ones on this device (data dictionary §7). */
  ReminderState: 'wiztime.reminderState',
  /** JSON `{ notified: { itemId: 'YYYY-MM-DD' } }`: important dates already announced by a system notification, and the calendar day (data dictionary §7). */
  ImportantDateNotified: 'wiztime.importantDateNotified',
  Sync: 'wiztime.sync',
  /** Identifier of the space this device last synced with; kept after logout, removed with the local data (data dictionary §7). */
  LastSpaceId: 'wiztime.lastSpaceId',
  /** '1' when the user chose "local use only" on the welcome page: the gate lets the app open without sync and no request goes to the server (data dictionary §7). */
  LocalOnly: 'wiztime.localOnly',
  /** '1' exposes `window.__wiztime` (see `app/testHooks.ts`). Set by end-to-end tests only. */
  TestHooks: 'wiztime.testHooks',
  /** '1' together with `TestHooks` skips the welcome gate so end-to-end specs run in local mode. */
  GateBypass: 'wiztime.gateBypass',
} as const;
export type StorageKey = (typeof StorageKey)[keyof typeof StorageKey];

export const FLAG_ON = '1';

/**
 * Removed from the dictionary (2026-10b §2.4): the quick-mark project is memory only now.
 * The key exists solely so start-up can delete what older versions left behind.
 */
export const LEGACY_QUICK_MARK_PROJECT_KEY = 'wiztime.quickMarkProjectId';

/**
 * Session-scoped keys (sessionStorage, per tab, data dictionary §7.1). `PickerDraft` stashes
 * the picker's unsaved content when the page goes hidden (2026-10b §5.4).
 */
export const SessionKey = {
  PickerDraft: 'wiztime.pickerDraft',
} as const;
export type SessionKey = (typeof SessionKey)[keyof typeof SessionKey];
